import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { cn, renderDisplayText } from "@/lib/utils";
import { LoadingLabel, Skel } from "@/components/home/TenantSkeletons";
import { GoogleGlyph } from "@/components/reviews/GoogleGlyph";
import type { ReviewsModel, ServicesModel, TrustItem } from "@/components/home/layouts/models";
import {
  HeroActions, HeroCopySkeleton, HeroTitle, Photo, RatingLine, ReviewExtras, ReviewerLine, StarRow,
  type HeroViewProps,
} from "@/components/home/layouts/shared";

/** Modern Minimal: huge left-aligned type, full-bleed photo band, numbered list. */
export function MinimalHero({ m, edit }: HeroViewProps) {
  const seq = (n: number) => ({ ["--seq" as string]: n });
  return (
    <section className="relative pb-0 pt-14 md:pt-20" aria-busy={!m.ready}>
      <div className="container mx-auto px-5 md:px-6">
        {m.ready ? (
          <>
            <div className="flex items-center justify-between gap-6 border-b tp-hairline pb-5 tp-seq" style={seq(0)}>
              <p className="tp-eyebrow">{m.eyebrow}</p>
              <span className="hidden text-xs uppercase tracking-[0.2em] text-muted-foreground sm:block">Book online</span>
            </div>
            <HeroTitle m={m} edit={edit} className="v-min-title tp-display tp-seq mt-8 md:mt-12" />
            <div className="mt-10 grid gap-8 md:mt-14 lg:grid-cols-12">
              <div className="tp-lead tp-seq max-w-[34rem] lg:col-span-6 lg:col-start-1" style={seq(2)}>
                {edit?.subtitle ?? <p>{m.subtitle}</p>}
              </div>
              <div className="tp-seq flex flex-col gap-6 lg:col-span-5 lg:col-start-8 lg:items-start" style={seq(3)}>
                <HeroActions m={m} edit={edit} />
                <RatingLine m={m} />
              </div>
            </div>
          </>
        ) : (
          <HeroCopySkeleton />
        )}
      </div>
      <div className="tp-hero-media relative mt-12 aspect-[4/3] w-full overflow-hidden bg-muted sm:aspect-[16/9] md:mt-20 lg:aspect-[21/9]">
        {m.heroPending ? <Skel className="absolute inset-0 h-full w-full rounded-none" /> : edit?.image ?? <Photo src={m.heroImage} alt="Professional cleaner at work" fetchPriority="high" decoding="async" />}
      </div>
    </section>
  );
}

export function MinimalTrust({ items }: { items: TrustItem[] }) {
  return (
    <section aria-label="Why customers trust us" className="border-b tp-hairline">
      <div className="container mx-auto px-5 md:px-6">
        <ul className="flex flex-col divide-y divide-foreground/10 md:flex-row md:divide-x md:divide-y-0">
          {items.map(({ key, Icon, title, subtitle }) => (
            <li key={key} className="flex flex-1 items-center gap-3 py-5 md:px-8 md:first:pl-0 md:last:pr-0">
              <Icon className="h-4 w-4 shrink-0 text-foreground" strokeWidth={1.5} />
              <span className="text-sm"><span className="font-semibold text-foreground">{title}</span> <span className="text-muted-foreground">{subtitle}</span></span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function MinimalServices({ s }: { s: ServicesModel }) {
  const [active, setActive] = useState(0);
  const shown = s.items[Math.min(active, Math.max(s.items.length - 1, 0))];
  return (
    <section className="tp-section">
      <div className="container mx-auto px-5 md:px-6">
        <div className="flex flex-wrap items-end justify-between gap-6 border-b border-foreground pb-6">
          <h2 className="tp-h2" data-reveal>{renderDisplayText(s.title)}</h2>
          <Link to={s.servicesHref} className="tp-link text-sm">All services <ArrowRight className="h-4 w-4" /></Link>
        </div>
        {s.loading ? (
          <div className="tp-skeleton mt-4 space-y-px" aria-busy="true">
            <LoadingLabel>Loading services</LoadingLabel>
            {[0, 1, 2].map((i) => <Skel key={i} className="h-24 w-full rounded-none" />)}
          </div>
        ) : (
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-16">
            <ol className="lg:col-span-7">
              {s.items.map((item, index) => (
                <li key={item.key} data-reveal className="border-b tp-hairline">
                  <Link
                    to={s.servicesHref}
                    onMouseEnter={() => setActive(index)}
                    onFocus={() => setActive(index)}
                    className="group grid grid-cols-[2.5rem_1fr_auto] items-baseline gap-x-4 py-7 md:grid-cols-[3.5rem_1fr_auto] md:py-9"
                  >
                    <span className="tp-numeral text-sm text-muted-foreground">{String(index + 1).padStart(2, "0")}</span>
                    <span>
                      <span className="tp-h3 block transition-transform duration-500 group-hover:translate-x-1.5">{renderDisplayText(item.title)}</span>
                      {item.description && <span className="mt-2 block max-w-md text-sm leading-relaxed text-muted-foreground">{item.description}</span>}
                    </span>
                    <span className="flex items-center gap-3 text-right text-sm font-medium text-foreground md:text-base">
                      {item.price}
                      <ArrowUpRight className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
            {shown && (
              <div className="hidden lg:col-span-5 lg:block">
                <div className="sticky top-28 mt-8 aspect-[4/5] overflow-hidden bg-muted">
                  {s.items.map((item, i) => (
                    <div key={item.key} className={cn("absolute inset-0 transition-opacity duration-700", i === active ? "opacity-100" : "opacity-0")} aria-hidden={i !== active}>
                      <Photo src={item.image} alt={i === active ? item.category : ""} loading="lazy" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export function MinimalReviews({ r, reviewFormHref }: { r: ReviewsModel; reviewFormHref?: string }) {
  return (
    <section className="tp-section border-t tp-hairline" aria-busy={r.pending}>
      <div className="container mx-auto px-5 md:px-6">
        <div className="flex flex-wrap items-end justify-between gap-6 border-b border-foreground pb-6">
          <h2 className="tp-h2" data-reveal>What our customers say</h2>
          {!r.pending && (
            <p className="flex items-center gap-3 text-sm">
              <span className="tp-numeral text-3xl text-foreground">{r.average.toFixed(1)}</span>
              <StarRow n={r.average} className="text-base" />
              <span className="text-muted-foreground">{r.countLine}</span>
            </p>
          )}
        </div>
        {r.pending ? (
          <div className="tp-skeleton mt-4 space-y-px" aria-hidden><LoadingLabel>Loading reviews</LoadingLabel>{[0, 1].map((i) => <Skel key={i} className="h-32 w-full rounded-none" />)}</div>
        ) : (
          <div>
            {r.items.map((item) => (
              <figure key={item.key} className="grid gap-5 border-b tp-hairline py-10 md:grid-cols-12 md:gap-10 md:py-14" data-reveal data-testid={item.google ? "google-review-card" : undefined}>
                <figcaption className="md:col-span-3">
                  <ReviewerLine item={item} nameClassName="text-base" />
                  <StarRow n={item.rating} className="mt-3 block text-sm" />
                  {item.google && <GoogleGlyph className="mt-3 h-4 w-4" />}
                </figcaption>
                <blockquote className="md:col-span-8 md:col-start-5">
                  <p className="font-display text-[clamp(1.35rem,1.1rem+1vw,2rem)] font-medium leading-snug tracking-tight text-foreground">{item.quote}</p>
                </blockquote>
              </figure>
            ))}
          </div>
        )}
        {!r.pending && <ReviewExtras google={r.google} reviewFormHref={reviewFormHref} className="mt-10" />}
      </div>
    </section>
  );
}
